import type { Urgency } from 'src/lib/support/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Button } from '@savvy-kit/components/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@savvy-kit/components/dialog';
import { Label } from '@savvy-kit/components/label';
import { RadioGroup, RadioGroupItem } from '@savvy-kit/components/radio-group';
import { Textarea } from '@savvy-kit/components/textarea';
import { AttachFileButton, FilePreviewList, WithFileUpload } from '@savvy-kit/patterns/file-attachment';
import { toast } from 'sonner';
import { useConfirmationDialog } from 'src/hooks/use-confirmation-dialog';
import { apiFetch } from 'src/utils/api-fetch';
import { buildAuthHeaders } from 'src/utils/auth-headers';
import { handleMutationError } from 'src/utils/handle-mutation-error';
import { DatadogRumAction } from 'src/utils/logging';

import { uploadToCloudinary } from './upload-to-cloudinary';

const MAX_DESCRIPTION_LENGTH = 5000;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp'] as const;

const URGENCY_OPTIONS: readonly { value: Urgency; label: string; description: string }[] = [
  {
    value: 'urgent',
    label: 'Urgent',
    description: "Pages our on-call product goalie. Use only when you're blocked from client work right now.",
  },
  { value: 'high', label: 'High', description: 'Reviewed first thing during business hours.' },
  { value: 'medium', label: 'Medium', description: 'Default — addressed in normal queue.' },
  { value: 'low', label: 'Low', description: 'Nice-to-have, no time pressure.' },
];

type Props = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
};

type SubmitPayload = {
  readonly description: string;
  readonly imageUrl?: string;
  readonly urgency: Urgency;
};

export function ReportIssueModal({ open, onOpenChange }: Props) {
  const showConfirmation = useConfirmationDialog();

  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [urgency, setUrgency] = useState<Urgency>('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [issueRef, setIssueRef] = useState<string | null>(null);
  // Bumped on every reset/open so WithFileUpload (which owns its own files
  // state via context) gets remounted and clears its internal list.
  const [uploadKey, setUploadKey] = useState(0);

  const resetForm = useCallback(() => {
    setDescription('');
    setFiles([]);
    setUrgency('medium');
    setIsSubmitting(false);
    setIsSubmitted(false);
    setIssueRef(null);
    setUploadKey((k) => k + 1);
  }, []);

  // Reset on next open after close
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (!wasOpenRef.current && open) {
      resetForm();
    }
    wasOpenRef.current = open;
  }, [open, resetForm]);

  const performSubmit = useCallback(
    async (payloadUrgency: Urgency) => {
      setIsSubmitting(true);

      // Capture file reference now — fetch should not be tied to modal lifecycle.
      const fileToUpload = files[0] ?? null;
      const trimmedDescription = description.trim();

      // Fire-and-track: do not abort on close. We use the regular fetch (no AbortController bound to
      // modal state) so the request completes even if the consumer un-mounts the dialog.
      try {
        let imageUrl: string | undefined;
        if (fileToUpload) {
          imageUrl = await uploadToCloudinary(fileToUpload);
        }

        const body: SubmitPayload = {
          description: trimmedDescription,
          urgency: payloadUrgency,
          ...(imageUrl ? { imageUrl } : {}),
        };

        const response = await apiFetch('/api/support/create', {
          method: 'POST',
          headers: buildAuthHeaders(),
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Response.json() returns any; narrow at the boundary
          const errorData = (await response.json().catch(() => ({}))) as { error?: string };
          throw new Error(errorData.error ?? 'Failed to submit issue');
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Response.json() returns any; narrow at the boundary
        const data = (await response.json()) as { issueIdentifier?: string };
        datadogRum.addAction(DatadogRumAction.ReportIssueSubmitted);
        setIsSubmitted(true);
        setIssueRef(data.issueIdentifier ?? null);
      } catch (error) {
        handleMutationError(error, 'There was an error submitting your issue. Please try again.');

        console.error('Failed to submit issue:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [description, files],
  );

  const handleSubmitClick = useCallback(() => {
    if (description.trim().length === 0) return;
    if (urgency === 'urgent') {
      showConfirmation({
        title: 'This will page our product goalie immediately — including nights and weekends.',
        description: (
          <div className="space-y-2 text-sm">
            <p>
              Use <em>Urgent</em> only when you're blocked from client work right now and can't wait until business
              hours. Examples: a client meeting in 30 minutes and you can't access account data; a system error
              preventing you from submitting a time-sensitive trade.
            </p>
            <p>
              For non-blocking issues, <em>High</em> will be reviewed first thing during business hours.
            </p>
          </div>
        ),
        confirmationLabel: 'Submit as Urgent',
        onConfirmation: () => {
          void performSubmit('urgent');
        },
      });
      return;
    }
    void performSubmit(urgency);
  }, [description, urgency, performSubmit, showConfirmation]);

  const submitDisabled = isSubmitting || description.trim().length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onOpenChange(false);
        } else {
          onOpenChange(true);
        }
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Report an Issue</DialogTitle>
        </DialogHeader>

        {isSubmitted ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">Issue Reported</p>
              <p className="text-sm text-secondary">
                Thanks for letting us know{issueRef ? ` (${issueRef})` : ''}. Our team is reviewing your issue and
                you&apos;ll receive updates via Slack DM.
              </p>
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={resetForm}>
                Report Another
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="report-issue-description">Describe the issue you&apos;re experiencing</Label>
              <p className="text-xs text-secondary">
                Include what you were trying to do, what happened, and any error messages you saw.
              </p>
              <Textarea
                id="report-issue-description"
                aria-label="Describe the issue"
                value={description}
                maxLength={MAX_DESCRIPTION_LENGTH}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Example: When I try to open a client's account page, I see a blank screen..."
                rows={6}
              />
            </div>

            <div className="space-y-2">
              <Label>Urgency</Label>
              <RadioGroup
                aria-label="Urgency"
                value={urgency}
                onValueChange={(value) => {
                  setUrgency(value);
                }}
              >
                {URGENCY_OPTIONS.map((opt) => (
                  <RadioGroupItem
                    key={opt.value}
                    id={`report-issue-urgency-${opt.value}`}
                    value={opt.value}
                    label={opt.label}
                    description={opt.description}
                  />
                ))}
              </RadioGroup>
            </div>

            <WithFileUpload
              key={uploadKey}
              allowedExtensions={ALLOWED_IMAGE_EXTS}
              maxSizeBytes={MAX_IMAGE_BYTES}
              onFilesChange={(next) => {
                // Single-attachment flow: only the first file is uploaded;
                // limit to one in the visible list to match.
                setFiles(next.slice(0, 1));
              }}
              onFileError={(message) => toast.error(message)}
              disabled={isSubmitting}
            >
              <div className="space-y-2">
                <Label>Attach a screenshot (optional)</Label>
                <AttachFileButton />
                <FilePreviewList files={files} />
              </div>
            </WithFileUpload>

            <DialogFooter>
              <Button onClick={handleSubmitClick} isLoading={isSubmitting} disabled={submitDisabled}>
                Submit
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
